export type ParallelModeConversion =
  | 'off'
  | 'to_major'
  | 'to_minor'
  | 'to_harmonic_minor'
  | 'to_dorian'
  | 'to_mixolydian';

export type KeyMode = 'major' | 'minor' | 'harmonic_minor' | 'dorian' | 'mixolydian';

export interface KeyContext {
  fifths: number;
  tonicPitchClass: number;
  sourceMode: KeyMode;
}

interface ModeDefinition {
  scalePitchClasses: number[];
  relativeMajorOffset: number;
  musicXmlMode: string;
}

export const MODE_CONVERSION_OPTIONS: Array<{ value: ParallelModeConversion; label: string }> = [
  { value: 'off', label: 'Original' },
  { value: 'to_major', label: 'Major' },
  { value: 'to_minor', label: 'Natural Minor' },
  { value: 'to_harmonic_minor', label: 'Harmonic Minor' },
  { value: 'to_dorian', label: 'Dorian' },
  { value: 'to_mixolydian', label: 'Mixolydian' },
];

const MODE_DEFINITIONS: Record<KeyMode, ModeDefinition> = {
  major: {
    scalePitchClasses: [0, 2, 4, 5, 7, 9, 11],
    relativeMajorOffset: 0,
    musicXmlMode: 'major',
  },
  minor: {
    scalePitchClasses: [0, 2, 3, 5, 7, 8, 10],
    relativeMajorOffset: 3,
    musicXmlMode: 'minor',
  },
  harmonic_minor: {
    scalePitchClasses: [0, 2, 3, 5, 7, 8, 11],
    relativeMajorOffset: 3,
    musicXmlMode: 'minor',
  },
  dorian: {
    scalePitchClasses: [0, 2, 3, 5, 7, 9, 10],
    relativeMajorOffset: -2,
    musicXmlMode: 'dorian',
  },
  mixolydian: {
    scalePitchClasses: [0, 2, 4, 5, 7, 9, 10],
    relativeMajorOffset: -7,
    musicXmlMode: 'mixolydian',
  },
};

const STEP_TO_PITCH_CLASS: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

const mod = (value: number, divisor: number): number => {
  return ((value % divisor) + divisor) % divisor;
};

export const normalizeMusicXmlKeyMode = (modeText: string | null | undefined): KeyMode | null => {
  if (!modeText) {
    return null;
  }
  const trimmed = modeText.trim().toLowerCase();
  if (trimmed === 'major') {
    return 'major';
  }
  if (trimmed === 'minor') {
    return 'minor';
  }
  if (trimmed === 'aeolian' || trimmed === 'natural minor' || trimmed === 'natural_minor') {
    return 'minor';
  }
  if (trimmed === 'harmonic minor' || trimmed === 'harmonic_minor') {
    return 'harmonic_minor';
  }
  if (trimmed === 'dorian') {
    return 'dorian';
  }
  if (trimmed === 'mixolydian') {
    return 'mixolydian';
  }
  return null;
};

const getDirectChild = (element: Element, tagName: string): Element | null => {
  for (const child of Array.from(element.children)) {
    if (child.tagName === tagName) {
      return child;
    }
  }
  return null;
};

export const getTargetModeForConversion = (
  sourceMode: KeyMode,
  conversion: ParallelModeConversion,
): KeyMode => {
  if (conversion === 'to_major') {
    return 'major';
  }
  if (conversion === 'to_minor') {
    return 'minor';
  }
  if (conversion === 'to_harmonic_minor') {
    return 'harmonic_minor';
  }
  if (conversion === 'to_dorian') {
    return 'dorian';
  }
  if (conversion === 'to_mixolydian') {
    return 'mixolydian';
  }
  return sourceMode;
};

const tonicPitchClassFromFifths = (fifths: number, mode: KeyMode): number => {
  const relativeMajorPitchClass = mod(7 * fifths, 12);
  return mod(relativeMajorPitchClass - MODE_DEFINITIONS[mode].relativeMajorOffset, 12);
};

const fifthsForRelativeMajorPitchClass = (
  relativeMajorPitchClass: number,
  referenceFifths: number,
): number => {
  let bestFifths = referenceFifths;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let fifths = -7; fifths <= 7; fifths += 1) {
    if (mod(7 * fifths, 12) !== relativeMajorPitchClass) {
      continue;
    }

    const distance = Math.abs(fifths - referenceFifths);
    if (distance < bestDistance) {
      bestFifths = fifths;
      bestDistance = distance;
    }
  }

  return bestFifths;
};

const transformedFifths = (fifths: number, sourceMode: KeyMode, targetMode: KeyMode): number => {
  if (sourceMode === targetMode) {
    return fifths;
  }

  const tonicPitchClass = tonicPitchClassFromFifths(fifths, sourceMode);
  const targetRelativeMajorPitchClass = mod(
    tonicPitchClass + MODE_DEFINITIONS[targetMode].relativeMajorOffset,
    12,
  );
  return fifthsForRelativeMajorPitchClass(targetRelativeMajorPitchClass, fifths);
};

const pitchClassDeltaForMode = (
  intervalPitchClass: number,
  sourceMode: KeyMode,
  targetMode: KeyMode,
): number => {
  const sourceScale = MODE_DEFINITIONS[sourceMode].scalePitchClasses;
  const targetScale = MODE_DEFINITIONS[targetMode].scalePitchClasses;
  const scaleDegree = sourceScale.indexOf(intervalPitchClass);

  if (scaleDegree === -1) {
    return 0;
  }

  const delta = targetScale[scaleDegree] - intervalPitchClass;
  if (delta > 6) {
    return delta - 12;
  }
  if (delta < -6) {
    return delta + 12;
  }
  return delta;
};

const applyKeyConversionAndGetContext = (
  keyElement: Element,
  conversion: ParallelModeConversion,
  fallbackMode: KeyMode | null,
): KeyContext | null => {
  const fifthsElement = getDirectChild(keyElement, 'fifths');
  if (!fifthsElement?.textContent) {
    return null;
  }

  const parsedFifths = Number.parseInt(fifthsElement.textContent, 10);
  if (Number.isNaN(parsedFifths)) {
    return null;
  }

  const modeElement = getDirectChild(keyElement, 'mode');
  const sourceMode = normalizeMusicXmlKeyMode(modeElement?.textContent) ?? fallbackMode ?? 'major';
  const targetMode = getTargetModeForConversion(sourceMode, conversion);
  const nextFifths = transformedFifths(parsedFifths, sourceMode, targetMode);

  if (nextFifths !== parsedFifths) {
    fifthsElement.textContent = `${nextFifths}`;
  }

  const targetMusicXmlMode = MODE_DEFINITIONS[targetMode].musicXmlMode;
  if (modeElement) {
    modeElement.textContent = targetMusicXmlMode;
  } else if (sourceMode !== targetMode) {
    const createdModeElement = keyElement.ownerDocument.createElement('mode');
    createdModeElement.textContent = targetMusicXmlMode;
    keyElement.appendChild(createdModeElement);
  }

  return {
    fifths: nextFifths,
    tonicPitchClass: tonicPitchClassFromFifths(parsedFifths, sourceMode),
    sourceMode,
  };
};

const readKeyContext = (keyElement: Element, fallbackMode: KeyMode | null): KeyContext | null => {
  const fifthsElement = getDirectChild(keyElement, 'fifths');
  if (!fifthsElement?.textContent) {
    return null;
  }

  const parsedFifths = Number.parseInt(fifthsElement.textContent, 10);
  if (Number.isNaN(parsedFifths)) {
    return null;
  }

  const modeElement = getDirectChild(keyElement, 'mode');
  const sourceMode = normalizeMusicXmlKeyMode(modeElement?.textContent) ?? fallbackMode ?? 'major';

  return {
    fifths: parsedFifths,
    tonicPitchClass: tonicPitchClassFromFifths(parsedFifths, sourceMode),
    sourceMode,
  };
};

export const getParallelModeConversionPitchDelta = (
  midiNote: number,
  keyContext: KeyContext | null,
  conversion: ParallelModeConversion,
): number => {
  if (conversion === 'off' || !keyContext) {
    return 0;
  }

  const targetMode = getTargetModeForConversion(keyContext.sourceMode, conversion);
  if (targetMode === keyContext.sourceMode) {
    return 0;
  }

  const absolutePitchClass = mod(midiNote, 12);
  const intervalPitchClass = mod(absolutePitchClass - keyContext.tonicPitchClass, 12);
  return pitchClassDeltaForMode(intervalPitchClass, keyContext.sourceMode, targetMode);
};

export const getMusicXmlKeyContextsByMeasure = (musicXml: string): Map<number, KeyContext> => {
  const contextsByMeasure = new Map<number, KeyContext>();
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(musicXml, 'application/xml');
  if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
    return contextsByMeasure;
  }

  for (const partElement of Array.from(xmlDoc.getElementsByTagName('part'))) {
    let currentKeyContext: KeyContext | null = null;
    let measureIndex = 0;

    for (const measureElement of Array.from(partElement.children)) {
      if (measureElement.tagName !== 'measure') {
        continue;
      }

      for (const childElement of Array.from(measureElement.children)) {
        if (childElement.tagName !== 'attributes') {
          continue;
        }

        for (const keyElement of Array.from(childElement.children)) {
          if (keyElement.tagName !== 'key') {
            continue;
          }
          const nextContext = readKeyContext(keyElement, currentKeyContext?.sourceMode ?? null);
          if (nextContext) {
            currentKeyContext = nextContext;
          }
        }
      }

      if (currentKeyContext && !contextsByMeasure.has(measureIndex)) {
        contextsByMeasure.set(measureIndex, currentKeyContext);
      }

      measureIndex += 1;
    }
  }

  return contextsByMeasure;
};

const transformNotePitch = (
  noteElement: Element,
  keyContext: KeyContext,
  conversion: ParallelModeConversion,
): void => {
  if (getDirectChild(noteElement, 'rest')) {
    return;
  }

  const pitchElement = getDirectChild(noteElement, 'pitch');
  if (!pitchElement) {
    return;
  }

  const stepElement = getDirectChild(pitchElement, 'step');
  const octaveElement = getDirectChild(pitchElement, 'octave');
  if (!stepElement?.textContent || !octaveElement?.textContent) {
    return;
  }

  const basePitchClass = STEP_TO_PITCH_CLASS[stepElement.textContent.trim().toUpperCase()];
  if (basePitchClass === undefined) {
    return;
  }

  const octave = Number.parseInt(octaveElement.textContent, 10);
  if (Number.isNaN(octave)) {
    return;
  }

  const alterElement = getDirectChild(pitchElement, 'alter');
  const alter = alterElement?.textContent ? Number.parseFloat(alterElement.textContent) : 0;
  if (Number.isNaN(alter)) {
    return;
  }

  const absolutePitchClass = mod(basePitchClass + alter, 12);
  const intervalPitchClass = mod(absolutePitchClass - keyContext.tonicPitchClass, 12);
  const targetMode = getTargetModeForConversion(keyContext.sourceMode, conversion);
  if (targetMode === keyContext.sourceMode) {
    return;
  }

  const delta = pitchClassDeltaForMode(intervalPitchClass, keyContext.sourceMode, targetMode);
  if (delta === 0) {
    return;
  }

  const nextAlter = alter + delta;
  if (nextAlter === 0) {
    if (alterElement) {
      pitchElement.removeChild(alterElement);
    }
  } else if (alterElement) {
    alterElement.textContent = `${nextAlter}`;
  } else {
    const newAlter = pitchElement.ownerDocument.createElement('alter');
    newAlter.textContent = `${nextAlter}`;
    pitchElement.insertBefore(newAlter, octaveElement);
  }

  const accidentalElement = getDirectChild(noteElement, 'accidental');
  if (accidentalElement) {
    noteElement.removeChild(accidentalElement);
  }
};

export const applyParallelModeConversionToMusicXml = (
  musicXml: string,
  conversion: ParallelModeConversion,
): string => {
  if (conversion === 'off') {
    return musicXml;
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(musicXml, 'application/xml');
  if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
    return musicXml;
  }

  for (const partElement of Array.from(xmlDoc.getElementsByTagName('part'))) {
    let currentKeyContext: KeyContext | null = null;

    for (const measureElement of Array.from(partElement.children)) {
      if (measureElement.tagName !== 'measure') {
        continue;
      }

      for (const childElement of Array.from(measureElement.children)) {
        if (childElement.tagName === 'attributes') {
          for (const keyElement of Array.from(childElement.children)) {
            if (keyElement.tagName !== 'key') {
              continue;
            }
            const nextContext = applyKeyConversionAndGetContext(
              keyElement,
              conversion,
              currentKeyContext?.sourceMode ?? null,
            );
            if (nextContext) {
              currentKeyContext = nextContext;
            }
          }
          continue;
        }

        if (childElement.tagName === 'note' && currentKeyContext) {
          transformNotePitch(childElement, currentKeyContext, conversion);
        }
      }
    }
  }

  return new XMLSerializer().serializeToString(xmlDoc);
};
